import * as React from 'react';
import { BUTTON, SIZES } from 'constants/components';
import CircleLoader from 'components/common/CircleLoader';
import { ButtonElement, ButtonLinkElement, TextElement, IconElement } from './Button.styled';
import { connect } from 'react-redux';
import { getFontAdjustmentValue } from 'store/accessibility/selectors';
import { RootAppState } from 'store/types';
import { FONT_SIZE_ADJUSTMENT_OPTIONS } from 'constants/accessibility';

export interface Layout {
  [BUTTON.LAYOUT.PRIMARY]: string;
  [BUTTON.LAYOUT.SECONDARY]: string;
  [BUTTON.LAYOUT.SIMPLE]: string;
}
export interface Size {
  [SIZES.SMALL]: string;
  [SIZES.MEDIUM]: string;
}
export interface Type {
  [BUTTON.TYPE.SUBMIT]: string;
  [BUTTON.TYPE.BUTTON]: string;
  [BUTTON.TYPE.RESET]: string;
}
type ButtonProps = {
  type?: string;
  suffixIcon?: string;
  disabled: boolean;
  children: React.ReactNode;
  onClick?(e?: any): void;
  layout: keyof Layout;
  size: keyof Size;
  textTransform?: string;
  tabIndex?: number;
  onBlur?(e?: any): void;
  onFocus?(e?: any): void;
  onKeyDown?(e?: any): void;
  link?: string;
  isLoading?: boolean;
  loaderIconSize: number;
  target?: string;
  inverted: boolean;
  theme?: any;
  id?: string;
  fontAdjustmentValue: FONT_SIZE_ADJUSTMENT_OPTIONS;
  isRtl?: boolean;
};

export const Button = (props: Readonly<ButtonProps>) => {
  const {
    disabled,
    onClick,
    children,
    type,
    layout,
    textTransform,
    tabIndex,
    link,
    onBlur,
    onFocus,
    isLoading,
    loaderIconSize,
    target,
    isRtl,
    ...other
  } = props;

  let { suffixIcon } = props;

  if (isRtl && suffixIcon?.endsWith('-right')) {
    suffixIcon = suffixIcon.replace('-right', '-left');
  }

  const Element: React.ReactType = link ? ButtonLinkElement : ButtonElement;
  return (
    <Element
      onClick={onClick}
      disabled={disabled}
      type={type}
      layout={layout}
      suffixIcon={suffixIcon}
      textTransform={textTransform}
      tabIndex={tabIndex}
      href={link}
      onBlur={onBlur}
      onFocus={onFocus}
      target={target || '_blank'}
      isLoading={isLoading}
      theme={props.theme}
      {...other}
    >
      <TextElement theme={props.theme} ellipsis layout={layout}>
        {children}
      </TextElement>
      {suffixIcon && (
        <IconElement name={suffixIcon} color="buttonTextColor" theme={props.theme} size={12} />
      )}
      {isLoading && <CircleLoader iconSize={loaderIconSize} />}
    </Element>
  );
};

Button.defaultProps = {
  layout: BUTTON.LAYOUT.PRIMARY,
  size: SIZES.MEDIUM,
  isLoading: false,
  loaderIconSize: 24,
  disabled: false,
  tabIndex: 0,
  inverted: false
};

function mapStateToProps(state: RootAppState) {
  return {
    fontAdjustmentValue: getFontAdjustmentValue(state)
  };
}

export default connect(mapStateToProps)(Button);
